import { type ReactNode } from 'react';
import { Sheet } from './Sheet';
import { Tabs } from './Tabs';

export interface InspectorTabItem {
  /** Stable identifier — matches `activeTab`. */
  value: string;
  /** Visible tab label (may include a count badge). */
  label: ReactNode;
  /** Body content rendered when this tab is active. */
  panel: ReactNode;
}

export interface InspectorTabsProps {
  /** When true the sidepanel slides in. */
  open: boolean;
  /** Fires when the user dismisses (Esc, scrim, or the close ×). */
  onClose: () => void;
  /** Mono eyebrow above the title (e.g. "Details", "Version history"). */
  kicker?: ReactNode;
  /** Sheet title above the tab strip. */
  title?: ReactNode;
  /** Muted sub-line under the title — dot-separated facets. */
  subtitle?: ReactNode;
  /** Tab definitions: label + panel body. */
  tabs: InspectorTabItem[];
  /** Currently-active tab value (controlled). */
  activeTab: string;
  /** Fires on tab swap. */
  onTabChange: (value: string) => void;
  /** Optional accessible name on the panel dialog (defaults to "Inspector"). */
  ariaLabel?: string;
  /** Widens the panel to `--inspector-w-wide` (the History drawer). */
  wide?: boolean;
  /**
   * Test prefix. The panel, tablist, and active panel slot get
   * `${dataTestId}-{panel,tabs,tabs-<value>,panel-<value>}` so specs can
   * resolve them deterministically.
   */
  dataTestId?: string;
}

/**
 * Right-anchored tabbed inspector — a thin composition of the global `Sheet`
 * primitive (which owns the scrim, slide, close ×, focus trap, and dismissal)
 * plus the `Tabs` strip as the sheet's header region. The active tab's panel is
 * the scrollable body.
 *
 * Used by the V2 document / work-item editors (Properties / Work items /
 * Comments) and the History drawer. The parent owns `open` (driven by the
 * savebar rail toggle) and `activeTab` (so the active tab survives re-render).
 *
 * CSS: `pn-sheet-*` (shell, from Sheet) + `pn-inspector-tabs-strip` (the tab
 * strip). The panel keeps `data-open` + the `${dataTestId}-{panel,tabs,…}`
 * testids that the editor/history e2e specs key on.
 */
export function InspectorTabs({
  open,
  onClose,
  kicker,
  title,
  subtitle,
  tabs,
  activeTab,
  onTabChange,
  ariaLabel = 'Inspector',
  wide = false,
  dataTestId
}: InspectorTabsProps) {
  const activePanel = tabs.find((t) => t.value === activeTab);
  const tid = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : undefined;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      kicker={kicker}
      title={title}
      subtitle={subtitle}
      wide={wide}
      ariaLabel={ariaLabel}
      dataTestId={dataTestId}
      bodyRole="tabpanel"
      bodyTestId={tid(`panel-${activeTab}`)}
      header={
        <Tabs
          items={tabs.map((t) => ({ value: t.value, label: t.label }))}
          value={activeTab}
          onChange={onTabChange}
          label={ariaLabel}
          className="pn-inspector-tabs-strip"
          dataTestId={tid('tabs')}
        />
      }
    >
      {activePanel ? activePanel.panel : null}
    </Sheet>
  );
}
